import React from "react";
import Link from "next/link";
import { LearnLayout } from "@/components/layout/LearnLayout";
import { Star, Zap, Clock, ShieldCheck, CheckCircle } from "lucide-react";
import { useNewUniversity } from "@/lib/university/UniversityProvider";

export default function PremiumSupport() {
  const { uni } = useNewUniversity();

  return (
    <LearnLayout
      title="Premium Support for High-Achieving Law Students: Structure, Speed, and Calm Under Pressure"
      description="Premium support is about better planning, faster feedback cycles, exam readiness, and avoiding burnout for UK law students. It is not about shortcuts."
      slug="premium-support"
      relatedArticles={[
        { title: "AI Study Assistant", slug: "ai-study-assistant" },
        { title: "Mark My Paper", slug: "mark-my-paper" },
      ]}
    >
      <div className="prose prose-indigo max-w-none">
        <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 font-serif">
          Premium Support for High-Achieving Law Students: Structure, Speed, and
          Calm Under Pressure
        </h1>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Doing your best work in law takes more than knowing the material; it
          takes a sensible way of working through a heavy term. Our{" "}
          <strong>Premium Support</strong> features are for students who want to
          study with more structure (faster feedback cycles and clearer
          planning) without burning out. They support how you work; they do not
          promise a particular grade.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
          What high achievers do differently
        </h2>
        <p>
          The students who cope best with law tend to treat their time as their
          scarcest resource. They don't simply work harder; they work more
          deliberately, around four habits:
        </p>
        <ul className="space-y-4 mb-10">
          <li className="flex gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-600 shrink-0" />{" "}
            <strong>Earlier Planning:</strong> Starting the revision runway 6
            weeks before the first exam.
          </li>
          <li className="flex gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-600 shrink-0" />{" "}
            <strong>Practice Under Pressure:</strong> Shifting from reading to
            timed practice as early as possible.
          </li>
          <li className="flex gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-600 shrink-0" />{" "}
            <strong>Tight Feedback Loops:</strong> Using critique to refine
            their writing style and logical structure.
          </li>
          <li className="flex gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-600 shrink-0" />{" "}
            <strong>Sustainability:</strong> Protecting sleep and maintaining
            consistency to avoid final-week burnout.
          </li>
        </ul>

        <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
          The “exam runway” plan (6 weeks)
        </h2>
        <p>
          Organise your transition from learning to responding with this
          structured timeline:
        </p>
        <div className="bg-indigo-50 border-l-4 border-indigo-600 p-6 my-10 rounded-r-xl">
          <ul className="list-none pl-0 space-y-4 mb-0">
            <li>
              <strong>Weeks 1–2:</strong> Consolidate seminar and lecture notes
              into high-level issue maps.
            </li>
            <li>
              <strong>Weeks 3–4:</strong> Move into timed practice and review
              sessions.
            </li>
            <li>
              <strong>Weeks 5–6:</strong> Focus on identified weak spots,
              increasing your speed and maintaining calm.
            </li>
          </ul>
        </div>
        <p>
          The goal is to build a "mini pack" for every topic: 1 page of ISSUE
          MAP + 3 pillar cases + 2 common pitfalls marks are often lost on.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
          Faster feedback loops for essays
        </h2>
        <p>
          Don't wait until the final draft to find a flaw in your reasoning. Use
          our Premium tools to move through{" "}
          <strong>Outline → Critique → Rewrite Outline → Draft</strong>. Testing
          your thesis and counterarguments early means less padding and sharper
          legal application by the time you write.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
          For Law Students
        </h2>
        <p>
          We understand the specific pressure points of the {uni.shortName} academic
          year, particularly the workload spikes that occur mid-term and just
          before Easter. Our support aims to help you manage these without
          losing your momentum.
        </p>
        <p className="text-sm italic text-gray-500">
          Note: Durmah is an independent study companion and is not affiliated
          with {uni.displayName}.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
          How Durmah helps
        </h2>
        <p>
          Premium users get priority workflows, structured assignment stages,
          and faster feedback tools. The approach is deliberately tutor-led and
          ethical: it is there to help you study with more discipline and
          understanding. The work, and the credit for it, stay yours.
        </p>
        <div className="mt-8">
          <Link href="/login">
            <button className="bg-indigo-600 text-white font-bold py-3 px-8 rounded-xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-200">
              View Plans
            </button>
          </Link>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-16 mb-6 pt-10 border-t">
          FAQ
        </h2>
        <div className="space-y-6">
          <div>
            <h4 className="font-bold text-gray-900">
              Does premium support mean someone writes for me?
            </h4>
            <p className="text-gray-600 text-sm m-0">
              No. Our philosophy is built on ethical study assistance. We
              provide the tools to help <em>you</em> write better, faster, and
              more clearly, but you remain the sole author of your work.
            </p>
          </div>
          <div>
            <h4 className="font-bold text-gray-900">
              What should I focus on 10 days before exams?
            </h4>
            <p className="text-gray-600 text-sm m-0">
              Consolidate your issue maps and practice 15-minute planning
              sessions for every past paper question you can find.
            </p>
          </div>
          <div>
            <h4 className="font-bold text-gray-900">
              How can I improve legal analysis quickly?
            </h4>
            <p className="text-gray-600 text-sm m-0">
              Focus on the "Application" part of IRAC. High achievers spend the
              most time explaining *why* the rule leads to a specific conclusion
              in this specific scenario.
            </p>
          </div>
          <div>
            <h4 className="font-bold text-gray-900">
              How do I avoid burnout in revision season?
            </h4>
            <p className="text-gray-600 text-sm m-0">
              Set a firm "stop time" every night. Consistency is more powerful
              than 18-hour study binges that leave you exhausted the next day.
            </p>
          </div>
        </div>

        <p className="mt-20 text-[10px] text-gray-400 text-center uppercase tracking-widest">
          Durmah is an independent study companion and is not affiliated with
          {uni.displayName}.
        </p>
      </div>
    </LearnLayout>
  );
}
